// 알림 빠른 설정 팝업 (신규 스키마: tb_alert_type + tb_buy_alert/tb_sell_alert)
import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchAlertTypes,
  fetchBuyAlert, saveBuyAlert,
  fetchSellAlert, saveSellAlert,
} from '../../api/alert';
import './AlertQuickPopup.css';

// buyId 또는 sellId 중 하나는 반드시 전달
const AlertQuickPopup = ({
  buyId,
  sellId,
  ticker,
  stockName,
  accountName,
  turnNo,
  onClose,
  onSaved,
}) => {
  const [alertTypes,  setAlertTypes]  = useState([]);
  const [alertData,   setAlertData]   = useState({
    alert_type_id_up:   null,
    alert_type_id_down: null,
    alert_enabled:      true,
  });
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [typePopup,   setTypePopup]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [typesRes, alertRes] = await Promise.all([
        fetchAlertTypes(),
        buyId ? fetchBuyAlert(buyId) : fetchSellAlert(sellId),
      ]);
      setAlertTypes(typesRes.data || []);
      const d = alertRes.data || {};
      setAlertData({
        alert_type_id_up:   d.alert_type_id_up   ?? null,
        alert_type_id_down: d.alert_type_id_down ?? null,
        alert_enabled:      d.alert_enabled      ?? true,
      });
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [buyId, sellId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        alert_type_id_up:   alertData.alert_type_id_up   || null,
        alert_type_id_down: alertData.alert_type_id_down || null,
        alert_enabled:      alertData.alert_enabled,
      };
      if (buyId)  await saveBuyAlert(buyId, payload);
      else        await saveSellAlert(sellId, payload);
      onSaved?.();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  // 상승 알림: UP 또는 BOTH 카테고리 유형
  const upTypes   = alertTypes.filter(t => t.is_active && ['UP', 'BOTH'].includes(t.alert_category));
  // 하락 알림: DOWN 또는 BOTH 카테고리 유형
  const downTypes = alertTypes.filter(t => t.is_active && ['DOWN', 'BOTH'].includes(t.alert_category));

  const turnLabel = turnNo ? `${turnNo}차` : '';

  const metricLabel = (t) => {
    if (!t) return '';
    const m = { PROFIT_RATE: '수익률', PRICE: '시세', DIFF_RATE: '등락률' };
    return m[t.alert_metric] || t.alert_metric;
  };

  const typeOptionLabel = (t) =>
    `${t.type_name} (${metricLabel(t)})`;

  return (
    <>
    <div className="aqp-overlay">
      <div className="aqp-popup">
        <div className="aqp-hd">
          🔔 알림 설정
          <button className="aqp-close" onClick={onClose}>✕</button>
        </div>
        <div className="aqp-sub">
          {stockName || ticker}
          {accountName && <span className="aqp-acct"> {accountName}</span>}
          {turnLabel   && <span className="aqp-turn"> {turnLabel}</span>}
        </div>

        {loading ? (
          <div className="aqp-loading">조회중...</div>
        ) : (
          <div className="aqp-bd">
            {/* 알림 활성 토글 */}
            <div className="aqp-enabled-row">
              <label className="aqp-enabled-label">
                <input
                  type="checkbox"
                  checked={alertData.alert_enabled}
                  onChange={e => setAlertData(p => ({ ...p, alert_enabled: e.target.checked }))}
                />
                <span>알림 활성</span>
              </label>
            </div>

            {/* 상승 알림 */}
            <div className="aqp-row">
              <span className="aqp-row-label aqp-up">상승 알림</span>
              <select
                className="aqp-select"
                value={alertData.alert_type_id_up ?? ''}
                onChange={e => setAlertData(p => ({
                  ...p,
                  alert_type_id_up: e.target.value ? parseInt(e.target.value) : null,
                }))}
              >
                <option value="">선택안함</option>
                {upTypes.map(t => (
                  <option key={t.id} value={t.id}>{typeOptionLabel(t)}</option>
                ))}
              </select>
            </div>

            {/* 하락 알림 */}
            <div className="aqp-row">
              <span className="aqp-row-label aqp-down">하락 알림</span>
              <select
                className="aqp-select"
                value={alertData.alert_type_id_down ?? ''}
                onChange={e => setAlertData(p => ({
                  ...p,
                  alert_type_id_down: e.target.value ? parseInt(e.target.value) : null,
                }))}
              >
                <option value="">선택안함</option>
                {downTypes.map(t => (
                  <option key={t.id} value={t.id}>{typeOptionLabel(t)}</option>
                ))}
              </select>
            </div>

            {alertTypes.length === 0 && (
              <div className="aqp-hint">
                알림 유형이 없습니다. 아래 버튼으로 먼저 유형을 만드세요.
              </div>
            )}
          </div>
        )}

        <div className="aqp-ft">
          <button
            className="btn btn-sm btn-outline aqp-type-btn"
            onClick={() => setTypePopup(true)}
          >
            알림 유형 관리 ▶
          </button>
          <div className="aqp-ft-right">
            <button className="btn btn-sm btn-outline" onClick={onClose}>닫기</button>
            <button
              className="btn btn-sm btn-primary"
              onClick={handleSave}
              disabled={saving || loading}
            >
              {saving ? '저장중...' : '저장'}
            </button>
          </div>
        </div>
      </div>
    </div>

    {typePopup && (() => {
      // 동적 import로 순환참조 방지
      const AlertTypePage = require('../../pages/admin/AlertTypePage').default;
      return (
        <AlertTypePage
          isModal
          onClose={() => {
            setTypePopup(false);
            // 알림 유형 목록 새로고침
            fetchAlertTypes().then(r => setAlertTypes(r.data || [])).catch(() => {});
          }}
        />
      );
    })()}
    </>
  );
};

export default AlertQuickPopup;
